import {
  type LocationQuery,
  LocationQueryEnum,
  RegionType,
} from "@b2bportal/air-shopping-api";
import type {
  FlightLocationSuggestion,
  FlightSearchValues,
} from "@b2bportal/core-flights";
import { IdEnum, type ITripTerminus } from "@b2bportal/core-types";
import { logger, suggestionToITripTerminus } from "@b2bportal/core-utilities";
import { fetchLocationAutocomplete } from "@hopper-b2b/api";
import { TripCategory } from "@overrides/types";
import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  buildFlightShopValuesFromQueryParams,
  buildMulticityFlightShopValuesFromQueryParams,
  type MulticityParsedParams,
  type SingleOrRoundTripParsedParams,
} from "@tenant/flights";

export const initializeSearchState = createAsyncThunk<
  FlightSearchValues,
  SingleOrRoundTripParsedParams | MulticityParsedParams
>("flightsShop/initializeSearchState", async (urlParameters, thunkAPI) => {
  const params = urlParameters;

  try {
    if (params.tripCategory === TripCategory.MULTI_CITY)
      return initializeMulticitySearchState(params);
    return initializeSingleRouteSearchState(params);
  } catch (error) {
    logger.debug(`Error fetching search locations. ${error}`);
    return thunkAPI.rejectWithValue({ error: String(error) });
  }
});

const initializeSingleRouteSearchState = async (
  params: SingleOrRoundTripParsedParams
): Promise<FlightSearchValues> => {
  const shopValues = buildFlightShopValuesFromQueryParams(params);
  const { origin: originCode, destination: destinationCode } = params;

  if (originCode == null || destinationCode == null) {
    throw new Error("Origin or destination is not set");
  }

  const [origin, destination] = await Promise.all([
    fetchLocationWithFallback(originCode),
    fetchLocationWithFallback(destinationCode),
  ]);

  return {
    ...shopValues,
    multicityRoutes: [
      {
        origin,
        destination,
        departureDate: shopValues.departureDate,
      },
      {
        origin: undefined,
        destination: undefined,
        departureDate: undefined,
      },
    ],
    origin,
    destination,
  };
};

export const initializeMulticitySearchState = async (
  params: MulticityParsedParams
) => {
  const shopValues = buildMulticityFlightShopValuesFromQueryParams(params);

  const multicityRoutes = await Promise.all(
    shopValues.multicityRoutes.map(async (route) => {
      if (route.origin == null || route.destination == null) {
        throw new Error("Origin or destination is not set");
      }
      const [origin, destination] = await Promise.all([
        fetchLocationWithFallback(route.origin),
        fetchLocationWithFallback(route.destination),
      ]);
      return {
        ...route,
        origin,
        destination,
      };
    })
  );
  const newSearchState: FlightSearchValues = {
    ...shopValues,
    multicityRoutes,
    origin: multicityRoutes[0]?.origin,
    destination: multicityRoutes[0]?.destination,
    departureDate: multicityRoutes[0]?.departureDate,
  };
  return newSearchState;
};

export const fetchLocation = async (
  locationCode: string
): Promise<ITripTerminus | undefined> => {
  const requestBody: LocationQuery = {
    LocationQuery: LocationQueryEnum.Label,
    l: locationCode,
  };
  const { categories } = await fetchLocationAutocomplete(requestBody);

  const suggestion = categories
    .flatMap((category) => category.results)
    .find(
      (result) =>
        result.id.Id === IdEnum.Flight && result.id.code.code === locationCode
    );

  return suggestion == null ? undefined : suggestionToITripTerminus(suggestion);
};

const fetchLocationWithFallback = async (
  code: string
): Promise<ITripTerminus> => {
  const result = await fetchLocation(code);
  return result ?? locationCodeToITripTerminus(code);
};

const locationCodeToITripTerminus = (code: string): ITripTerminus => {
  return {
    id: {
      code: {
        regionType: RegionType.airport,
        code,
      },
      Id: IdEnum.Flight,
    },
    label: code,
    trackingProperties: {},
  };
};

export const fetchFlightLocationSuggestion = async (
  locationCode: string
): Promise<FlightLocationSuggestion | undefined> => {
  const requestBody: LocationQuery = {
    LocationQuery: LocationQueryEnum.Label,
    l: locationCode,
  };
  const { categories } = await fetchLocationAutocomplete(requestBody);

  const suggestion = categories
    .flatMap((category) => category.results)
    .find(
      (result): result is FlightLocationSuggestion =>
        result.id.Id === IdEnum.Flight && result.id.code.code === locationCode
    );

  return suggestion;
};
